import { useCurrentStateAndParams } from '@uirouter/react';
import { Clock } from 'lucide-react';
import { isNumber } from 'lodash';

import { localizeDate } from '@/react/common/date-utils';

import { WidgetBody } from '@@/Widget';
import { Widget } from '@@/Widget/Widget';
import { DetailsTable } from '@@/DetailsTable/DetailsTable';
import { ExternalBadge } from '@@/Badge/ExternalBadge';
import { Link } from '@@/Link';
import { SystemBadge } from '@@/Badge/SystemBadge';
import { InlineLoader } from '@@/InlineLoader';

import { appOwnerLabel } from '../constants';
import { useNamespaceQuery } from '../../namespaces/queries/useNamespaceQuery';
import { Application } from '../ListView/ApplicationsDatatable/types';

export function CustomResourceSummaryWidget({
  application,
  isInitialLoading,
}: {
  application?: Application;
  isInitialLoading: boolean;
}) {
  const stateAndParams = useCurrentStateAndParams();
  const {
    params: { namespace, name, endpointId: environmentId },
  } = stateAndParams;

  const namespaceData = useNamespaceQuery(environmentId, namespace);
  const isSystemNamespace = namespaceData.data?.IsSystem;

  const externalApplication = !application?.Metadata?.labels?.[appOwnerLabel];

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget>
          <WidgetBody>
            {isInitialLoading && <InlineLoader>Loading...</InlineLoader>}
            {application && (
              <DetailsTable dataCy="k8sAppDetail-table">
                <tr>
                  <td>Name</td>
                  <td>
                    <div
                      className="flex items-center gap-x-2"
                      data-cy="k8sAppDetail-appName"
                    >
                      {name}
                      {externalApplication && !isSystemNamespace && (
                        <ExternalBadge />
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Namespace</td>
                  <td>
                    <div
                      className="flex items-center gap-x-2"
                      data-cy="k8sAppDetail-resourcePoolName"
                    >
                      <Link
                        to="kubernetes.resourcePools.resourcePool"
                        data-cy="k8sAppDetail-namespaceLink"
                        params={{ id: namespace }}
                      >
                        {namespace}
                      </Link>
                      {isSystemNamespace && <SystemBadge />}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Application type</td>
                  <td data-cy="k8sAppDetail-appType">
                    {application.Kind} ({application.ApplicationType})
                  </td>
                </tr>
                {application.DeploymentType &&
                  isNumber(application.TotalPodsCount) &&
                  isNumber(application.RunningPodsCount) && (
                    <tr>
                      <td>Status</td>
                      <td data-cy="k8sAppDetail-appType">
                        {application.DeploymentType}
                        <code className="ml-1">
                          {application.RunningPodsCount}
                        </code>{' '}
                        / <code>{application.TotalPodsCount}</code>
                      </td>
                    </tr>
                  )}
                <tr>
                  <td>Creation</td>
                  <td>
                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        className="flex items-center gap-1"
                        data-cy="k8sAppDetail-creationDate"
                      >
                        <Clock />
                        {localizeDate(new Date(application.CreationDate))}
                      </span>
                    </div>
                  </td>
                </tr>
              </DetailsTable>
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}
